import { useState } from 'react';

export type RecordFieldDef = {
  key: string;
  label: string;
  editable: boolean;
};

type Props = {
  title: string;
  status: 'found' | 'new' | 'internal';
  summary: string;
  fields: RecordFieldDef[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  // Provided only for existing ("found") records - saves edits to the record.
  onSave?: () => Promise<void>;
  // Optional hint shown above the fields (e.g. record is in the trash).
  note?: string;
};

const STATUS_LABEL: Record<Props['status'], string> = {
  found: 'In CRM',
  new: 'Will be created',
  internal: 'Internal',
};

export const RecordSection = ({
  title,
  status,
  summary,
  fields,
  values,
  onChange,
  onSave,
  note,
}: Props) => {
  // Both cards open by default so the user sees the full record at a glance.
  const [expanded, setExpanded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave();
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="record">
      <button
        type="button"
        className="record-head"
        onClick={() => setExpanded((open) => !open)}
      >
        <span className="chev">{expanded ? '▾' : '▸'}</span>
        <span className="record-title">{title}</span>
        <span className={`record-status ${status}`}>
          {summary || STATUS_LABEL[status]}
        </span>
      </button>

      {expanded && (
        <div className="record-body">
          {note && <div className="banner hint">{note}</div>}
          {fields.map((field) => (
            <div className="record-field" key={field.key}>
              <span className="record-flabel">{field.label}</span>
              {field.editable ? (
                <input
                  className={values[field.key] ? undefined : 'missing'}
                  value={values[field.key] ?? ''}
                  placeholder="Add…"
                  onChange={(event) => {
                    setSaved(false);
                    onChange(field.key, event.target.value);
                  }}
                />
              ) : (
                <span
                  className={values[field.key] ? 'record-fvalue' : 'missing-text'}
                >
                  {values[field.key] || 'missing'}
                </span>
              )}
            </div>
          ))}

          {onSave && status === 'found' && (
            <button
              type="button"
              className="secondary"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save to record'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
